import { Component, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { LeadDetailsService } from '../../../services/lead-details.service';
import { ReferralDataService } from '../../../services/referral-data.service';
import { SectionsService } from '../../../services/sections.service';
import { AuthorizationUtility } from '../../../shared/authorization/auth.utility';
import { AuthorizationService } from '../../../shared/authorization/authorization.service';
import { PAGE_CONSTANTS } from '../../../shared/constants/page.constants';
import { STATIC_DATA } from '../../../shared/constants/static-data.constants';
import { SharedDataService } from '../../../shared/services/shared-data-service';
import { ParticipantLeadsListComponent } from '../../participant-leads-list/participant-leads-list.component';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { AuthorizeButtonDirective } from '../../../shared/authorization/authorizeButton.directive';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { ListTableComponent } from '../../../shared/components/list-table/list-table.component';
import { UserService } from '../../../services/user.service';
import { StorageService } from '../../../services/storage.service';
import { LookupValueItem } from '../../../interfaces/lookup.interface';
import { AutoFocusHeaderDirective } from '../../../shared/directives/auto-focus-header.directive';

@Component({
  selector: 'ppl-participant-enrollment-list',
  standalone: true,
  imports: [ListTableComponent,
    NzIconModule,
    NzButtonModule,
    RouterModule,
    AuthorizeButtonDirective,
    ButtonComponent,
    AutoFocusHeaderDirective],
  templateUrl: './participant-enrollment-list.component.html',
  styleUrl: './participant-enrollment-list.component.css'
})
export class ParticipantEnrollmentListComponent extends ParticipantLeadsListComponent implements OnInit{
  override sharedDataKey: string = STATIC_DATA.SHARED_PARTICIPANT_ENROLLMENT_DATA;
  override pageId: string = PAGE_CONSTANTS.PARTICIPANT_ENROLLMENT_DATA_GRID_PAGE;
  contactStsReasonLkpList: LookupValueItem[] = [];
  drReviewNeededSearchValues = [
    { lkpValueName: 'Yes', lkpValueCode: 'Yes' },
    { lkpValueName: 'No', lkpValueCode: 'No' },
  ];
  // lkpValueCode must match the stored association_status_lkp text (the search does a Contains on the
  // aggregated statuses). TODO: ideally source from lookup domain 38 instead of hardcoding.
  associationStatusSearchValues = [
    {lkpValueName: 'Association Active', lkpValueCode: 'Association Active'},
    {lkpValueName: 'Association Active with Gap', lkpValueCode: 'Association Active with Gap'},
    {lkpValueName: 'In PPL Review', lkpValueCode: 'In PPL Review'},
    {lkpValueName: 'In Progress', lkpValueCode: 'In Progress'},
    {lkpValueName: 'On Hold Health Assessment', lkpValueCode: 'On Hold Health Assessment'},
    {lkpValueName: 'Paperwork Complete', lkpValueCode: 'Paperwork Complete'},
    {lkpValueName: 'Returned', lkpValueCode: 'Returned'},
    {lkpValueName: 'Suspended - Training', lkpValueCode: 'Suspended - Training'},
  ];
  // Association type options: label = display name, value = provider_type_lkp code sent to the backend
  // (matched exactly against the consumer's associations). The displayed association_type still returns names.
  associationTypeSearchValues = [
    {lkpValueName: 'Designated Representative', lkpValueCode: 'AR'},
    {lkpValueName: 'Personal Assistant', lkpValueCode: 'IR'},
    {lkpValueName: 'Legal Guardian', lkpValueCode: 'LG'},
    {lkpValueName: 'Power of Attorney', lkpValueCode: 'POA'},
  ];
  // Day thresholds for "Days Since Last Timesheet"; used with the Greater than operator (e.g. > 90 days).
  daysSinceLastTimesheetSearchValues = [
    {lkpValueName: '30 days', lkpValueCode: '30'},
    {lkpValueName: '60 days', lkpValueCode: '60'},
    {lkpValueName: '90 days', lkpValueCode: '90'},
    {lkpValueName: '120 days', lkpValueCode: '120'},
    {lkpValueName: '150 days', lkpValueCode: '150'},
    {lkpValueName: '180 days', lkpValueCode: '180'},
    {lkpValueName: '365 days', lkpValueCode: '365'},
  ];
  // Preset windows for the "Authorization Expiring Within" filter; lkpValueCode is the number of days
  // sent to the backend (auth to_date between today and today + N days).
  authExpiringSearchValues = [
    {lkpValueName: '30 days', lkpValueCode: '30'},
    {lkpValueName: '60 days', lkpValueCode: '60'},
    {lkpValueName: '90 days', lkpValueCode: '90'},
  ];
  applicationStatusSearchValues: LookupValueItem[] = [];

  constructor(
    router: Router,
    sectionService: SectionsService,
    referralDataService: ReferralDataService,
    sharedDataService: SharedDataService,
    authUtility: AuthorizationUtility,
    authService: AuthorizationService,
    userService: UserService,
    private leadDetailsService: LeadDetailsService,
    private storageService: StorageService
  ) {
    super(
      router,
      sectionService,
      referralDataService,
      sharedDataService,
      userService,
      authUtility,
      authService,
    );
  }

  override onNewLead(): void {
    this.router.navigate(['/referralLogic']);
    this.sectionService.setFlow('providers');
    this.referralDataService.clearReferrralData();
  }

  override ngOnInit(): void {
    this.contactStsReasonLkpList = this.lookupService.getLookupValue('contact_status_reason_lkp');
    this.applicationStatusSearchValues = this.lookupService.getLookupValue('Participant_Enrollment_status');
    super.ngOnInit()
  }

  onNewReferral() {
    this.router.navigate(['/newReferral']);
    this.sectionService.setFlow('providerEnrollment');
    this.userService.setLeadEntry('');
    this.referralDataService.clearReferrralData();
  }

  onNewEnrollment() {
    this.router.navigate(['/newReferral']);
    this.storageService.setParticipantId("");
    this.sectionService.setFlow('participantEnrollment');
    this.userService.setLeadEntry('');
    this.userService.setSelectedLead('');
    this.referralDataService.clearReferrralData();
  }

  override rowClicked(rowDetail: any) {
    const program = this.userService.getSelectedProgram();
    this.userService.setLeadEntry('participantEnrollment');
    this.sectionService.setFlow('participantEnrollment');
    this.referralDataService.clearReferrralData();

    this.userService.setSelectedLead(rowDetail.participant_id);  //rowDetail.referral_participant_id
    this.userService.setParticipantPplId(rowDetail.participant_ppl_id)
    if (rowDetail.application_status_lkp == 'New Application' || rowDetail.application_status_lkp == 'In Progress') {
      this.leadDetailsService
        .fetchLeadDetailsApi(
          'participantEnrollment',
          program ? program.program_code : '',
          rowDetail.participant_id
        )
        .subscribe((data: any) => {
          this.referralDataService.setReferralDetails(data.responsedata);
          setTimeout(() => {
            this.storageService.setParticipantId(data?.responsedata?.infoData.participantId );
            this.storageService.setParticipantPPLId(data.responsedata?.infoData.participantPPLId);
            this.storageService.setTrackingNumber(data.responsedata?.infoData.trackingNumber);
            this.router.navigate(['/newReferral']);
          }, 200);
        });
    } else {
      setTimeout(() => {
        this.router.navigate(['/home/participantEnrollmentList/details/'], { state: { navigateTo: '/home/participantEnrollmentList' } });
      }, 200);
    }
  }

}
